import {
  AwardVouchersInput,
  EarningMechanismConfigurationInput,
  Offer_Engagement_Type,
  OfferUpdateInput,
} from '@occc/graphql';
import {
  DirtyFieldsType,
  GetConfigureFormOperations,
  Operation,
} from '@occc-app/offerHelpers/types';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { makeEarningMechanismCreateOperation } from '@occc-app/offerHelpers/operations/earningMechanism';
import { prepareProductChallengeCreateInput } from '@occc-app/routes/Challenge/components/ChallengeConfigureForm/utils/earningMechanismOperations';
import { getLimitsOperations, LimitType } from '@occc/feat-limits';
import { earningMechanismInputCreator } from '@occc-app/offerHelpers/earningMechanismInputCreator';
import { createOperationToDisableTriggerOnProgress } from '@occc-app/routes/CampaignComponents/components/TriggerOnProgress/utils';
import {
  getMinimumPurchaseConstraintOperations,
  getPaymentMethodConstraint,
  getPaymentMethodConstraintOperations,
  getStoreLocationConstraint,
  getStoreLocationGroupConstraint,
  makeRequiresActivationConstraintOperations,
} from '@occc/feat-constraints';

import { getOfferNameAndDescriptionUpdateOperations } from '@occc-app/routes/Offer/components/OfferConfigureForm/utils/offerNameAndDescriptionUpdateOperations';
import { getOfferFulfillmentPriorityUpdateOperations } from '@occc-app/offerHelpers/operations/getOfferFulfillmentPriorityUpdateOperations';
import {
  SweepstakesAwardVouchersFormValues,
  SweepstakesConfigureFormValues,
  SweepstakesType,
} from './types';

import { getScanDataConfigurationOperation } from '../../offerHelpers/operations/getScanDataConfigurationOperation';
import { getOfferCustomDataOperation } from '../../offerHelpers/operations/getOfferCustomDataOperation';

export type EarningMechanismCreateInput = Pick<
  EarningMechanismConfigurationInput,
  | 'productPurchase'
  | 'varietyPurchase'
  | 'varietyPurchaseOverTime'
  | 'totalSpendOverTime'
  | 'pointSpend'
>;

type ConfiguredSweepstakesFormValues = Exclude<
  SweepstakesConfigureFormValues,
  { sweepstakesType: null }
>;

const getAwardVouchersInput = (
  awardVouchers: SweepstakesAwardVouchersFormValues,
): { awardVouchers: AwardVouchersInput } => ({
  awardVouchers: {
    voucherCount: awardVouchers.voucherCount,
    voucheredOfferId: awardVouchers.voucheredOffer.id,
    voucherExpirationDays: awardVouchers.isEnabled
      ? awardVouchers.voucherExpirationDays
      : undefined,
  },
});

type GetEarningMechanismCreateInput = (
  values: ConfiguredSweepstakesFormValues,
) => EarningMechanismCreateInput;

const getEarningMechanismCreateInput: GetEarningMechanismCreateInput = (values) => {
  const { sweepstakesType } = values;
  const awardInput = getAwardVouchersInput(values.awardVouchers);

  if (values.sweepstakesType === SweepstakesType.ProductPurchase) {
    const { eligibleProductGroups, purchaseThreshold } = values.productPurchase;

    return earningMechanismInputCreator.createProductPurchase(
      purchaseThreshold,
      eligibleProductGroups,
      awardInput,
    );
  }

  if (values.sweepstakesType === SweepstakesType.VarietyChallenge) {
    return prepareProductChallengeCreateInput(values.productChallenge, awardInput);
  }

  if (values.sweepstakesType === SweepstakesType.SpendBonus) {
    const { ineligibleProductGroups, accumulationThreshold, stayVisibleWhileInProgress } =
      values.spendBonus;

    return earningMechanismInputCreator.createTotalSpendOverTime(
      accumulationThreshold,
      ineligibleProductGroups,
      awardInput,
      stayVisibleWhileInProgress?.isEnabled,
    );
  }

  if (values.sweepstakesType === SweepstakesType.Reward) {
    return earningMechanismInputCreator.createPointSpend(
      values.pointSpend.pointCost,
      awardInput.awardVouchers,
    );
  }

  throw new Error(`${sweepstakesType} type or its configuration is not supported or not provided`);
};

const getEarningMechanismOperations = (
  offerId: string,
  values: SweepstakesConfigureFormValues,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  const operations: Operation[] = [];

  const fields: (keyof SweepstakesConfigureFormValues)[] = [
    'sweepstakesType',
    'awardVouchers',
    'productChallenge',
    'productPurchase',
    'spendBonus',
    'pointSpend',
  ];

  const hasAtLeastOneDirtyField = fields.some((field) =>
    isFieldDirty<SweepstakesConfigureFormValues>(dirtyFields, field),
  );

  if (!hasAtLeastOneDirtyField) {
    return operations;
  }

  if (values.earningMechanism?.id) {
    operations.push(makeDeleteOperation('earningMechanismDelete', values.earningMechanism.id));
  }

  // TODO: it might be better to check if we need this check or not
  // Is it possible to say that we always have this value defined at this stage?
  if (!values.sweepstakesType) {
    throw new Error('sweepstakesType is not defined');
  }

  const configuration = getEarningMechanismCreateInput(values);
  operations.push(makeEarningMechanismCreateOperation(offerId, configuration));

  return operations;
};

export const getConfigureFormOperations: GetConfigureFormOperations<
  SweepstakesConfigureFormValues
> = (offerId, offer, values, dirtyFields) => {
  const engagementTypeToSet =
    values.sweepstakesType === SweepstakesType.Reward
      ? Offer_Engagement_Type.SweepstakesPurchasable
      : Offer_Engagement_Type.SweepstakesTransactional;

  const additionalOperations: Operation[] = [];

  if (offer?.engagementType !== engagementTypeToSet) {
    const input: OfferUpdateInput = {
      id: offerId,
      engagementType: engagementTypeToSet,
    };

    const operation: Operation = {
      name: 'offerUpdate',
      variables: {
        input,
      },
    };

    additionalOperations.push(operation);

    const storeLocationConstraint = getStoreLocationConstraint(offer);
    const storeLocationGroupConstraint = offer
      ? getStoreLocationGroupConstraint(offer.constraints)
      : undefined;

    if (engagementTypeToSet === Offer_Engagement_Type.SweepstakesPurchasable) {
      if (storeLocationConstraint?.id) {
        additionalOperations.push(
          makeDeleteOperation('constraintDelete', storeLocationConstraint.id),
        );
      }

      if (storeLocationGroupConstraint?.id) {
        additionalOperations.push(
          makeDeleteOperation('constraintDelete', storeLocationGroupConstraint.id),
        );
      }
    }
  }

  const isOverTimeEarningMechanism = values.sweepstakesType === SweepstakesType.SpendBonus;

  // https://stuzo.atlassian.net/browse/OCA-1516
  // when an offer subtype is changed to a non-Progress EM
  // set "triggerOnProgress" to "false" if needed
  if (
    offer?.earningMechanism?.__typename === 'EarningMechanismTotalSpendOverTime' &&
    !isOverTimeEarningMechanism &&
    offer.triggerPreferences.triggerOnProgress
  ) {
    const operation = createOperationToDisableTriggerOnProgress(offerId, offer.triggerPreferences);
    additionalOperations.push(operation);
  }

  const earningMechanismOperations = getEarningMechanismOperations(offerId, values, dirtyFields);

  const supportsActivationConstraint = values.sweepstakesType !== SweepstakesType.Reward;

  const activationConstraintOperations = supportsActivationConstraint
    ? makeRequiresActivationConstraintOperations(offerId, values.activation, dirtyFields, offer)
    : [];

  const awardCountLimitsOperations = getLimitsOperations(
    offerId,
    values.awardCount,
    dirtyFields,
    LimitType.AwardCount,
    { offer },
  );

  const supportsPaymentMethodConstraint = [
    SweepstakesType.ProductPurchase,
    SweepstakesType.VarietyChallenge,
    SweepstakesType.SpendBonus,
  ].some((type) => type === values.sweepstakesType);

  const paymentMethodConstraintOperations = supportsPaymentMethodConstraint
    ? getPaymentMethodConstraintOperations(offerId, offer, values, dirtyFields)
    : [];

  const isMinimumPurchaseConstraintSupported =
    values.sweepstakesType === SweepstakesType.VarietyChallenge &&
    values.productChallenge?.earningMechanism === 'varietyPurchase';

  const minimumPurchaseConstraintOperations = isMinimumPurchaseConstraintSupported
    ? getMinimumPurchaseConstraintOperations(offerId, values, dirtyFields, offer)
    : getMinimumPurchaseConstraintOperations(
        offerId,
        {
          minimumPurchaseConstraints: {
            isEnabled: false,
            items: [],
          },
        },
        dirtyFields,
        offer,
      );

  const existingPaymentMethodConstraint = getPaymentMethodConstraint(offer);
  if (existingPaymentMethodConstraint && !supportsPaymentMethodConstraint) {
    // https://stuzo.atlassian.net/browse/OCA-1737
    // payment method constraint should be removed if we change type to Reward which
    // doesn't support this constraint
    additionalOperations.push(
      makeDeleteOperation('constraintDelete', existingPaymentMethodConstraint.id),
    );
  }

  const customDataOperation = getOfferCustomDataOperation({
    dirtyFields,
    values,
    offerId,
  });
  if (customDataOperation) {
    additionalOperations.push(customDataOperation);
  }

  const scanDataConfigurationOperation = getScanDataConfigurationOperation({
    offerId,
    values,
    dirtyFields,
  });
  if (scanDataConfigurationOperation) {
    additionalOperations.push(scanDataConfigurationOperation);
  }

  additionalOperations.push(
    ...getOfferNameAndDescriptionUpdateOperations(offerId, values, dirtyFields),
  );

  additionalOperations.push(
    ...getOfferFulfillmentPriorityUpdateOperations(offerId, values, dirtyFields),
  );

  return [
    ...additionalOperations,
    ...earningMechanismOperations,
    ...awardCountLimitsOperations,
    ...activationConstraintOperations,
    ...paymentMethodConstraintOperations,
    ...minimumPurchaseConstraintOperations,
  ];
};
